import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { DeliveryNotesV3Service } from 'api-v3/services';
import {
    setLoading,
    setError,
    setDeliveryNotes,
    DeliveryNoteWithOrgSiteKey,
    isLoading,
} from '@app/stores/deliveryNotesStore';
import {
    searchState,
    updatePaginationFromApiResponse,
    resetPaginationToEmpty,
    cancelOngoingRequests,
} from '@app/stores/searchStore';
import { SortType, DeliveryNotesFilterProperties } from 'api-v3/models';
import { ABORT_SIGNAL } from '@app/shared/constants/http-context-tokens';

@Injectable({
    providedIn: 'root',
})
export class DeliveryNotesPaginatedService {
    constructor(private deliveryNotesV3Service: DeliveryNotesV3Service) {}

    async loadDeliveryNotesPaginated(
        organizationSiteKey: string
    ): Promise<void> {
        if (isLoading()) {
            return;
        }

        setLoading(true);
        setError(null);

        // Cancel any ongoing requests and get new abort signal
        const abortSignal = cancelOngoingRequests();

        try {
            const currentSearchState = searchState();

            // hack search-text for boolean search-strings
            let searchText = currentSearchState.searchText;
            if (
                currentSearchState.searchText &&
                (currentSearchState.searchText === 'Ja' ||
                    currentSearchState.searchText === 'Yes')
            ) {
                searchText = 'true';
            } else if (
                currentSearchState.searchText &&
                (currentSearchState.searchText === 'Nein' ||
                    currentSearchState.searchText === 'No')
            ) {
                searchText = 'false';
            }

            // Get multi-site selection and showClosed filter
            const { getSelectedLocationKeys, getShowClosed } = await import(
                '@app/stores/appStore'
            );
            const selectedSiteKeys = getSelectedLocationKeys('consignor');
            const showClosed = getShowClosed();

            // Don't make API call if no organization sites are selected
            if (selectedSiteKeys.length === 0) {
                return;
            }

            if (abortSignal?.aborted) {
                return;
            }

            const sortBy = currentSearchState.sort.sortBy;

            // Use valid sort field or fall back to default - ensure type safety
            const validSortBy = sortBy as any;

            // Default to desc for dateCreated (newest first), use user's direction for other fields
            const sortDirection =
                validSortBy === 'dateCreated' && !sortBy
                    ? SortType.Desc // Default to newest first when no sort is specified
                    : currentSearchState.sort.sortDirection === 'asc'
                      ? SortType.Asc
                      : SortType.Desc;

            // Handle uppercase conversion for specific filter properties
            let filterValue = searchText || undefined;
            let filterProperty = currentSearchState.selectedFilterKey?.keyName;

            if (
                filterValue &&
                filterProperty &&
                (filterProperty === 'bundleNumber' ||
                    filterProperty === 'tourNumber')
            ) {
                filterValue = filterValue.toUpperCase();
            }

            let params = {
                organizationSiteKey,
                organizationSiteKeys:
                    selectedSiteKeys.length > 0 ? selectedSiteKeys : undefined,
                limit: currentSearchState.pagination.limit,
                offset: currentSearchState.pagination.offset,
                sort: sortDirection,
                sortBy: validSortBy,
                filterProperty: filterProperty
                    ? (filterProperty as DeliveryNotesFilterProperties)
                    : undefined,
                filterValue: filterValue,
                filterNotClosed: !showClosed,
                filterOnlyDiscrepancies: undefined,
            };

            // handle special-case "discrepancies" in filter
            if (filterProperty === 'discrepancies') {
                params = {
                    ...params,
                    filterOnlyDiscrepancies: filterValue,
                    filterProperty: undefined as any,
                    filterValue: undefined,
                };
            }

            if (abortSignal?.aborted) {
                return;
            }

            // Create HttpContext with abort signal for proper HTTP request cancellation
            const context = new HttpContext().set(ABORT_SIGNAL, abortSignal);

            // This will go through the HTTP interceptors and can be cancelled
            const response = await lastValueFrom(
                this.deliveryNotesV3Service.getDeliveryNotesMetadataInPoolV3(
                    params,
                    context
                )
            );

            if (response && response.data) {
                // Map the response to include orgSiteKey
                const notesWithOrgSiteKey: DeliveryNoteWithOrgSiteKey[] =
                    response.data.map(note => ({
                        ...note,
                        orgSiteKey: organizationSiteKey,
                    }));

                setDeliveryNotes(notesWithOrgSiteKey);

                // Update pagination info in centralized searchStore
                updatePaginationFromApiResponse(response.paginationInfo);
            }
        } catch (error: any) {
            if (
                error?.name === 'AbortError' ||
                error?.message === 'Subscription has been cancelled' ||
                abortSignal?.aborted
            ) {
                return;
            }

            // Extract request ID from error if available
            const requestId =
                error?.headers?.get?.('x-request-id') ||
                error?.error?.requestId ||
                null;
            const errorMessage = requestId
                ? `GENERAL.ERRORS.FAILED_TO_LOAD_DELIVERY_NOTES|${requestId}`
                : 'GENERAL.ERRORS.FAILED_TO_LOAD_DELIVERY_NOTES';

            setError(errorMessage);

            // Reset pagination to empty state on error
            resetPaginationToEmpty();
        } finally {
            if (!abortSignal?.aborted) {
                setLoading(false);
            }
        }
    }
}
